import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Save } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CareerTiers, getCareerTiers, updateCareerTiers } from "@/services/companyClassificationApi";

import { bucketTier, maxCount, validateTiers } from "./classification";

const TIER_BAR_CLASS = {
  below: "bg-muted-foreground/30",
  progressing: "bg-blue-500",
  strong: "bg-emerald-500",
} as const;

export function TiersTab() {
  const navigate = useNavigate();
  const [tiers, setTiers] = useState<CareerTiers | null>(null);
  const [progressing, setProgressing] = useState("");
  const [strong, setStrong] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const apply = (data: CareerTiers) => {
    setTiers(data);
    setProgressing(String(data.progressing_min));
    setStrong(String(data.strong_min));
  };

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      apply(await getCareerTiers(navigate));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const validation = validateTiers(progressing, strong);
  const dirty = tiers !== null && (Number(progressing) !== tiers.progressing_min || Number(strong) !== tiers.strong_min);

  const handleSave = async () => {
    if (validation) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      apply(await updateCareerTiers({ progressing_min: Number(progressing), strong_min: Number(strong) }, navigate));
      setSuccess("Paliers enregistrés — appliqués par les pods back sous ~30 s, sans recalcul.");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setSaving(false);
    }
  };

  const p = Number(progressing) || 0;
  const s = Number(strong) || 0;
  const peak = tiers ? maxCount(tiers.distribution) : 0;
  const counts = tiers?.counts;
  const evaluable = counts ? counts.below + counts.progressing + counts.strong : 0;
  const share = (value: number) => (evaluable ? `${Math.round((value / evaluable) * 100)} %` : "—");

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Paliers A-player</CardTitle>
          <CardDescription>
            Score de progression de carrière minimum (0-100) de chaque palier. Appliqué à la lecture : aucun
            recalcul des candidats.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <Label htmlFor="tier-progressing">« Progression » à partir de</Label>
              <Input
                id="tier-progressing"
                type="number"
                min={1}
                max={100}
                value={progressing}
                onChange={(e) => setProgressing(e.target.value)}
                disabled={loading}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tier-strong">« Forte progression » à partir de</Label>
              <Input
                id="tier-strong"
                type="number"
                min={1}
                max={100}
                value={strong}
                onChange={(e) => setStrong(e.target.value)}
                disabled={loading}
              />
            </div>
          </div>
          {validation && !loading && <p className="text-sm text-destructive">{validation}</p>}
          {error && <p className="text-sm text-destructive">{error}</p>}
          {success && <p className="text-sm text-emerald-600">{success}</p>}
          {counts && (
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>Non évaluables : <strong>{counts.not_evaluable.toLocaleString("fr-FR")}</strong></div>
              <div>Sous les paliers : <strong>{counts.below.toLocaleString("fr-FR")}</strong> ({share(counts.below)})</div>
              <div>Progression : <strong>{counts.progressing.toLocaleString("fr-FR")}</strong> ({share(counts.progressing)})</div>
              <div>Forte progression : <strong>{counts.strong.toLocaleString("fr-FR")}</strong> ({share(counts.strong)})</div>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Les effectifs par palier sont calculés avec les valeurs enregistrées ; les couleurs du graphique suivent
            les valeurs saisies.
          </p>
        </CardContent>
        <CardFooter>
          <Button onClick={handleSave} disabled={saving || loading || !!validation || !dirty}>
            <Save className="mr-1 h-4 w-4" /> {saving ? "Enregistrement…" : "Enregistrer"}
          </Button>
        </CardFooter>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Distribution des scores</CardTitle>
          <CardDescription>Candidats enrichis évaluables, par tranche de 10 points.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex h-48 items-end gap-2">
            {tiers?.distribution.map((bucket) => (
              <div key={bucket.bucket} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
                <span className="text-[10px] text-muted-foreground">{bucket.count}</span>
                <div
                  className={`w-full rounded-t ${TIER_BAR_CLASS[bucketTier(bucket.bucket, p, s)]}`}
                  style={{ height: `${peak ? Math.max(2, (bucket.count / peak) * 100) : 2}%` }}
                  title={`${bucket.bucket} : ${bucket.count}`}
                />
                <span className="text-[10px] text-muted-foreground">{bucket.bucket}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
